import { useContext, useState, useRef } from 'react';
import InputField from 'components/common/input-field';
import Select from 'components/common/select';
import { IRoles } from 'store/features/administratorRoleSlice';
import {
  countryList,
  stateList,
  cityList,
  onlyNumbers,
  scrollUp,
  initSideModal,
} from 'helpers/utils';
import { useAppSelector, useAppDispatch } from 'hooks/useAppDispatchSelector';
import { IOrganizationAssociateParking } from 'store/features/administratorRoleSlice';
import { AppContext } from 'context/app-context';
import CommonLayout from './common-layout';
import { AddCompany, ICompany, editCompany } from 'store/features/companySlice';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import { ReactComponent as EditIcon } from 'assets/icon/pencil.svg';
import { ReactComponent as DeleteIcon } from 'assets/icon/trash.svg';
import toast from 'react-hot-toast';
import { setSideModalData } from 'store/features/modelSlice';
import { IAddress } from 'helpers/types';
import { useTranslation } from 'react-i18next';

interface IError extends Omit<ICompany, '_id' | 'headquarters'> {
  headquarters: string;
}

export interface IAddEditOtherStation extends IAddress {
  isEdited?: boolean;
  index: number;
}

const initCompnay: ICompany = {
  id: '',
  name: '',
  fiscal_number: '',
  activity: '',
  headquarters: null,
  other_stations: [],
};

const AddEditCompany = () => {
  const dispatch = useAppDispatch();
  const { setLoader } = useContext(AppContext);
  const { sideModal } = useAppSelector(state => state.modal);
  const { companyDetails } = useAppSelector(state => state.company);
  const {
    content: { formFor, callBackFun },
  } = sideModal;
  const [company, setCompany] = useState<ICompany>(
    formFor === 'EDIT' && companyDetails ? companyDetails : initCompnay,
  );
  const [otherStation, setOtherStation] = useState<IAddEditOtherStation | null>(null);
  const [error, setError] = useState<Partial<IError>>({});
  const [dropdownSearch, serDropdownSearch] = useState<string>('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const getErrorData = () => {
    return {
      name: !company?.name?.trim() ? t('companies.add_edit.name_message') : '',
      fiscal_number: !company?.fiscal_number?.trim()
        ? t('companies.add_edit.fiscal_number_message')
        : '',
      activity: !company?.activity?.trim() ? t('companies.add_edit.activity_message') : '',
      headquarters:
        !company?.headquarters ||
        (company?.headquarters &&
          (!company?.headquarters?.address1?.trim() ||
            !company?.headquarters?.country?.trim() ||
            !company?.headquarters?.state?.trim() ||
            !company?.headquarters?.city?.trim() ||
            !company?.headquarters?.zipcode))
          ? t('companies.add_edit.headquarter_address_message')
          : '',
    };
  };
  const isValid = () => {
    if (
      !company?.name?.trim() ||
      !company.fiscal_number?.trim() ||
      !company?.activity?.trim() ||
      !company?.headquarters ||
      (company?.headquarters &&
        (!company?.headquarters?.address1?.trim() ||
          !company?.headquarters?.country?.trim() ||
          !company?.headquarters?.state?.trim() ||
          !company?.headquarters?.city?.trim() ||
          !company?.headquarters?.zipcode))
    ) {
      const output = getErrorData();
      setError(output);
      scrollUp(scrollRef);
      return false;
    } else {
      const output = getErrorData();
      setError(output);
      return true;
    }
  };
  const addAdministratorRole = () => {
    setLoader(true);

    const request: Omit<ICompany, 'id'> = {
      fiscal_number: company?.fiscal_number || '',
      activity: company?.activity || '',
      name: company?.name || '',
      headquarters: company?.headquarters || null,
      other_stations: company?.other_stations.map(data => {
        let add: IAddress = {
          address1: data?.address1 || '',
          city: data?.city || '',
          state: data?.state || '',
          country: data?.country || '',
          zipcode: data?.zipcode || '',
        };

        if (data?.address2?.trim()) {
          add = { ...add, address2: data?.address2 || '' };
        }
        return add;
      }),
    };
    if (!company?.headquarters?.address2?.trim()) {
      delete request.headquarters?.address2;
    }
    dispatch(AddCompany({ request: request }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('companies.add_edit.success_add_toast_message'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const editAdministratorRoles = () => {
    setLoader(true);
    const request: Omit<ICompany, 'id'> = {
      fiscal_number: company?.fiscal_number || '',
      activity: company?.activity || '',
      name: company?.name || '',
      headquarters: company?.headquarters || null,
      other_stations: company?.other_stations.map(data => {
        let add: IAddress = {
          address1: data?.address1 || '',
          city: data?.city || '',
          state: data?.state || '',
          country: data?.country || '',
          zipcode: data?.zipcode || '',
        };

        if (data?.address2?.trim()) {
          add = { ...add, address2: data?.address2 || '' };
        }
        return add;
      }),
    };
    if (!company?.headquarters?.address2?.trim()) {
      delete request.headquarters?.address2;
    }
    dispatch(editCompany({ request: request, company_id: company?._id || '' }))
      .then(() => {
        callBackFun && callBackFun();
        toast.success(t('companies.add_edit.success_update_toast_message'));
        dispatch(setSideModalData(initSideModal));
        setLoader(false);
      })
      .catch(() => {
        setLoader(false);
      });
  };
  const handleSubmit = () => {
    if (!isValid()) {
      return;
    }
    formFor === 'EDIT' ? editAdministratorRoles() : addAdministratorRole();
  };
  const handleChange = (
    field: string,
    value: string | IOrganizationAssociateParking | IRoles,
    subField: string = '',
  ) => {
    let updatedData = { ...company };
    switch (field) {
      case 'headquarters': {
        const address: IAddress = {
          address1: updatedData?.headquarters?.address1 || '',
          address2: updatedData?.headquarters?.address2 || '',
          city: updatedData?.headquarters?.city || '',
          state: updatedData?.headquarters?.state || '',
          country: updatedData?.headquarters?.country || '',
          zipcode: updatedData?.headquarters?.zipcode || '',
          [subField]: value,
        };
        updatedData = {
          ...updatedData,
          headquarters: address,
        };
        break;
      }
      default: {
        updatedData = { ...updatedData, [field]: value };
        break;
      }
    }
    setCompany(updatedData);
  };
  const getStateList = (key: 'headquarters' | 'other_station') => {
    let selectedCountry = null;
    if (key === 'other_station') {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === otherStation?.country,
      );
    } else {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === company[key]?.country,
      );
    }

    return stateList(selectedCountry?.isoCode) || [];
  };
  const getSelectedState = (key: 'headquarters' | 'other_station') => {
    let selectedCountry = null;
    if (key === 'other_station') {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === otherStation?.country,
      );
    } else {
      selectedCountry = countryList.find(
        (data: { name: string }) => data.name === company[key]?.country,
      );
    }
    return (
      stateList(selectedCountry?.isoCode).find(
        (data: { name: string }) =>
          data.name === (key === 'other_station' ? otherStation?.state : company[key]?.state),
      ) || null
    );
  };
  const getCityList = (key: 'headquarters' | 'other_station') => {
    const selectedState = getSelectedState(key);
    return cityList(selectedState?.countryCode, selectedState?.isoCode) || [];
  };
  const getSelectedCity = (key: 'headquarters' | 'other_station') => {
    const selectedState = getSelectedState(key);
    return (
      cityList(selectedState?.countryCode, selectedState?.isoCode).find(
        (data: { name: string }) =>
          data.name === (key === 'other_station' ? otherStation?.city : company[key]?.city),
      ) || null
    );
  };
  const handleOtherStation = (field: string, value: string) => {
    setOtherStation({
      _id: otherStation?._id || '',
      address1: otherStation?.address1 || '',
      address2: otherStation?.address2 || '',
      city: otherStation?.city || '',
      state: otherStation?.state || '',
      zipcode: otherStation?.zipcode || '',
      country: otherStation?.country || '',
      isEdited: otherStation?.isEdited ? true : false,
      index: otherStation?.index || 0,
      [field]: value || '',
    });
  };
  const handleAddParkingAddress = async () => {
    if (!otherStation?.address1?.trim()) {
      toast.error(t('companies.add_edit.error_address_toast_message'));
      return;
    } else if (!otherStation?.country?.trim()) {
      toast.error(t('companies.add_edit.error_country_toast_message'));
      return;
    } else if (!otherStation?.state?.trim()) {
      toast.error(t('companies.add_edit.error_state_toast_message'));
      return;
    } else if (!otherStation?.city?.trim()) {
      toast.error(t('companies.add_edit.error_city_toast_message'));
      return;
    } else if (!otherStation?.zipcode) {
      toast.error(t('companies.add_edit.error_zipcode_toast_message'));
      return;
    }
    let updatedStations = [...company?.other_stations] || [];
    const addedAddress: IAddress = {
      address1: otherStation?.address1,
      address2: otherStation?.address2,
      city: otherStation?.city,
      state: otherStation?.state,
      country: otherStation?.country,
      zipcode: otherStation.zipcode,
    };
    if (otherStation.isEdited) {
      updatedStations = (company.other_stations || []).map((data, index) =>
        index === otherStation?.index ? addedAddress : data,
      );
    } else {
      updatedStations = [...updatedStations, { ...addedAddress }];
    }
    setCompany({
      ...company,
      other_stations: updatedStations || [],
    });
    setOtherStation(null);
  };
  const handleDeleteParkingAddress = async (index: number) => {
    let updatedData = [...company.other_stations].filter((data, i) => i !== index);
    setCompany({ ...company, other_stations: updatedData });
  };
  return (
    <CommonLayout
      onConfirm={() => handleSubmit()}
      confirmButtonProps={{
        label:
          formFor === 'EDIT'
            ? t('companies.add_edit.save_changes')
            : t('companies.add_edit.add_company'),
      }}
    >
      <div className="flex flex-col w-full h-full p-6">
        {/*Admin Role Inputs*/}
        <div className="flex flex-wrap -mx-3">
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('companies.tableHeaders.name')}
              </label>
              <InputField
                placeholder={t('companies.tableHeaders.name')}
                value={company?.name}
                onChange={val => handleChange('name', val)}
                error={error?.name || ''}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('companies.tableHeaders.fiscal_number')}
              </label>
              <InputField
                placeholder={t('companies.tableHeaders.fiscal_number')}
                value={company?.fiscal_number}
                onChange={val => handleChange('fiscal_number', val)}
                error={error?.fiscal_number || ''}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('companies.tableHeaders.activity')}
              </label>
              <InputField
                placeholder={t('companies.tableHeaders.activity')}
                value={company?.activity}
                onChange={val => handleChange('activity', val)}
                error={error?.activity || ''}
              />
            </div>
          </div>
          <div className="w-1/2 px-3"></div>
          <div className="w-full px-3">
            <label className="font-medium text-sm text-black w-full">
              {t('companies.add_edit.headquarter_address')}
            </label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS1')}
                value={company?.headquarters?.address1 || ''}
                onChange={val => handleChange('headquarters', val, 'address1')}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS2')}
                value={company?.headquarters?.address2 || ''}
                onChange={val => handleChange('headquarters', val, 'address2')}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.country')}
                selected={
                  countryList.find(
                    (data: { name: string }) => data.name === company?.headquarters?.country,
                  ) || { id: '00', name: company.headquarters?.country || '' }
                }
                setSelected={data => handleChange('headquarters', data?.name, 'country')}
                options={countryList.filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleChange('headquarters', val, 'country');
                }}
                dropdownPlacement="TOP"
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.state')}
                selected={
                  getSelectedState('headquarters') || {
                    id: '00',
                    name: company.headquarters?.state || '',
                  }
                }
                setSelected={data => handleChange('headquarters', data?.name, 'state')}
                options={getStateList('headquarters').filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleChange('headquarters', val, 'state');
                }}
                dropdownPlacement="TOP"
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.state')}
                selected={
                  getSelectedCity('headquarters') || {
                    id: '00',
                    name: company.headquarters?.city || '',
                  }
                }
                setSelected={data => handleChange('headquarters', data?.name, 'city')}
                options={getCityList('headquarters').filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleChange('headquarters', val, 'city');
                }}
                dropdownPlacement="TOP"
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('companies.add_edit.postalCode')}
                value={company?.headquarters?.zipcode || ''}
                onChange={val => {
                  if (val && !onlyNumbers.test(val)) {
                    return;
                  }
                  handleChange('headquarters', val, 'zipcode');
                }}
              />
            </div>
          </div>
          <div className="w-full px-3">
            {error.headquarters && (
              <p className=" font-regular text-xs text-red-500">{error.headquarters}</p>
            )}
          </div>
          <div className="w-full px-3">
            <label className="font-medium text-sm text-black w-full">
              {t('companies.tableHeaders.other_stations')}
            </label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS1')}
                value={otherStation?.address1 || ''}
                onChange={val => handleOtherStation('address1', val)}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('ADDRESS2')}
                value={otherStation?.address2 || ''}
                onChange={val => handleOtherStation('address2', val)}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.country')}
                selected={
                  countryList.find(
                    (data: { name: string }) => data.name === otherStation?.country,
                  ) || { id: '00', name: otherStation?.country || '' }
                }
                setSelected={data => handleOtherStation('country', data?.name)}
                options={countryList.filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                dropdownPlacement="TOP"
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleOtherStation('country', val);
                }}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.state')}
                selected={
                  getSelectedState('other_station') || { id: '00', name: otherStation?.state || '' }
                }
                setSelected={data => handleOtherStation('state', data?.name)}
                options={getStateList('other_station').filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                dropdownPlacement="TOP"
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleOtherStation('state', val);
                }}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <Select
                placeholder={t('companies.add_edit.city')}
                selected={
                  getSelectedCity('other_station') || { id: '00', name: otherStation?.city || '' }
                }
                setSelected={data => handleOtherStation('city', data?.name)}
                options={getCityList('other_station').filter(
                  coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
                )}
                dropdownPlacement="TOP"
                isSearchable
                search={dropdownSearch}
                setSearch={val => {
                  serDropdownSearch(val);
                  handleOtherStation('city', val);
                }}
              />
            </div>
          </div>
          <div className="w-1/4 px-3">
            <div className="mb-2">
              <InputField
                placeholder={t('companies.add_edit.postalCode')}
                value={otherStation?.zipcode || ''}
                onChange={val => {
                  if (val && !onlyNumbers.test(val)) {
                    return;
                  }
                  handleOtherStation('zipcode', val);
                }}
              />
            </div>
          </div>
          <div className="w-full px-3">
            <div className="flex flex-wrap -mx-3">
              <div className="w-1/2 px-3"></div>
              <div className="w-1/2 px-3 text-right">
                <div className="flex justify-end">
                  <PrimaryDarkButton
                    label={`${
                      otherStation?.isEdited
                        ? t('companies.add_edit.save_changes')
                        : t('companies.add_edit.add_station')
                    }`}
                    onClick={() => handleAddParkingAddress()}
                    height="h-7"
                    text="text-sm"
                    width="w-1/3"
                  />
                </div>
              </div>
              <div className="w-full px-3">
                <div className="flex flex-wrap rounded-2 mt-3 bg-bg-50 p-4 mb-3 border-1.5">
                  {(company?.other_stations || []).length > 0 ? (
                    (company?.other_stations || []).map((station, index) => (
                      <div
                        className="flex flex-col w-1/3 p-3"
                        key={`${station?._id || station?.address1}-${index}`}
                      >
                        <div className="flex items-center w-full">
                          {/* <label className="text-base font-medium flex-1">{`Address ${
                            index + 1
                          }`}</label> */}
                          <label className="text-base font-medium flex-1">
                            {t('companies.add_edit.addressline', { index: index + 1 })}
                          </label>
                          <div className="flex items-center gap-x-1">
                            <EditIcon
                              className="cursor-pointer"
                              height={16}
                              width={16}
                              onClick={() =>
                                setOtherStation(
                                  station ? { ...station, isEdited: true, index: index } : null,
                                )
                              }
                            />
                            <DeleteIcon
                              className="cursor-pointer"
                              height={16}
                              width={16}
                              onClick={() => handleDeleteParkingAddress(index || 0)}
                            />
                          </div>
                        </div>
                        <div className="flex flex-col w-full gap-x-2 ">
                          <div className="flex items-center flex-wrap break-words text-sm text-black2 w-full">{`${station?.address1}, ${
                            station?.address2 ? `${station?.address2},` : ''
                          } ${station?.city}, ${station?.state}, ${station?.country} - ${station?.zipcode}.`}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center py-4 px-1 w-full rounded-2 bg-bg-50">
                      <label className="text-base font-medium text-grey3 text-center w-full mx-auto">
                        {t('companies.add_edit.noaddressmessage')}
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
};

export default AddEditCompany;
